import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  User, 
  Mail, 
  Phone, 
  Building, 
  MapPin,
  Bell,
  Moon,
  Globe,
  Shield,
  HelpCircle,
  LogOut,
  ChevronRight,
  Camera,
  Save,
  Users,
  FileText,
  Package
} from 'lucide-react';
import { useThemeMode } from '../context/ThemeContext';
import Modal from '../components/Modal';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 80px;
  overflow-y: auto;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 50;
  min-height: 60px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text};

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  flex: 1;
  text-align: center;
`;

const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ProfileSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const AvatarContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const Avatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    width: 50px;
    height: 50px;
    color: white;
  }
`;

const CameraButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.text};
  color: white;
  border: 3px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
  }
`;

const UserName = styled.h2`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
`;

const UserRole = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const Section = styled.section`
  background: ${({ theme }) => theme.colors.white};
  margin-top: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 20px 8px;
  margin: 0;
`;

const FormGroup = styled.div`
  padding: 0 20px;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  gap: 10px;
  flex-wrap: wrap;

  &:last-child {
    border-bottom: none;
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.textSecondary};
    flex-shrink: 0;
  }

  @media (max-width: 400px) {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`;

const InputLabel = styled.label`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 80px;
  flex-shrink: 0;

  @media (max-width: 400px) {
    min-width: auto;
  }
`;

const Input = styled.input`
  flex: 1;
  border: none;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text};
  background: transparent;
  text-align: right;
  min-width: 0;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  @media (max-width: 400px) {
    text-align: center;
    width: 100%;
  }
`;

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
  }
`;

const SettingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  svg {
    width: 22px;
    height: 22px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const SettingText = styled.span`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text};
`;

const SettingRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const SettingValue = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Toggle = styled.button`
  width: 50px;
  height: 28px;
  border-radius: 14px;
  border: none;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : '#D1D5DB'};
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $active }) => $active ? '25px' : '3px'};
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: white;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: calc(100% - 40px);
  margin: 20px;
  padding: 14px;
  background: ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.dangerText};
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    opacity: 0.9;
  }
`;

const VersionText = styled.p`
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 20px;
  margin: 0;
`;

const Perfil = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useThemeMode();
  const [userData, setUserData] = useState({
    name: 'Dr. Carlos Mendoza',
    email: 'carlos.mendoza@drdesk.com',
    phone: '+52 555 123 4567',
    clinic: 'Clínica San Rafael',
    address: 'Av. Principal 123, CDMX'
  });

  const [settings, setSettings] = useState({
    notifications: true,
    language: 'Español'
  });

  // Estados para modales
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const handleInputChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSetting = (setting) => {
    setSettings(prev => ({ ...prev, [setting]: !prev[setting] }));
  };

  const handleSave = () => {
    setShowSaveModal(true);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    // Limpiar sesión
    localStorage.removeItem('drdesk_session');
    sessionStorage.removeItem('drdesk_session');
    navigate('/login');
  };

  return (
    <PageContainer>
      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={confirmLogout}
        title="Cerrar Sesión"
        message="¿Estás seguro de que deseas cerrar sesión?"
        type="warning"
        confirmText="Cerrar Sesión"
        cancelText="Cancelar"
        showCancel
      />

      <Modal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Perfil Guardado"
        message="Tu información de perfil se ha guardado correctamente."
        type="success"
        confirmText="Aceptar"
      />

      <Header>
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft />
        </BackButton>
        <HeaderTitle>Mi Perfil</HeaderTitle>
        <SaveButton onClick={handleSave}>
          <Save />
          Guardar
        </SaveButton>
      </Header>

      <ProfileSection>
        <AvatarContainer>
          <Avatar>
            <User />
          </Avatar>
          <CameraButton>
            <Camera />
          </CameraButton>
        </AvatarContainer>
        <UserName>{userData.name}</UserName>
        <UserRole>Administrador</UserRole>
      </ProfileSection>

      <Section>
        <SectionTitle>Información Personal</SectionTitle>
        <FormGroup>
          <InputRow>
            <User />
            <InputLabel>Nombre</InputLabel>
            <Input 
              type="text" 
              value={userData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </InputRow>
          <InputRow>
            <Mail />
            <InputLabel>Email</InputLabel>
            <Input 
              type="email" 
              value={userData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </InputRow>
          <InputRow>
            <Phone />
            <InputLabel>Teléfono</InputLabel>
            <Input 
              type="tel" 
              value={userData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />
          </InputRow>
          <InputRow>
            <Building />
            <InputLabel>Clínica</InputLabel>
            <Input 
              type="text" 
              value={userData.clinic}
              onChange={(e) => handleInputChange('clinic', e.target.value)}
            />
          </InputRow>
          <InputRow>
            <MapPin />
            <InputLabel>Dirección</InputLabel>
            <Input 
              type="text" 
              value={userData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
            />
          </InputRow>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>Administración</SectionTitle>
        <SettingItem onClick={() => navigate('/gestion-personal')}>
          <SettingLeft>
            <Users />
            <SettingText>Gestión de Personal</SettingText>
          </SettingLeft>
          <SettingRight>
            <ChevronRight />
          </SettingRight>
        </SettingItem>
        <SettingItem onClick={() => navigate('/gestion-formularios')}>
          <SettingLeft>
            <FileText />
            <SettingText>Formularios de Pacientes</SettingText>
          </SettingLeft>
          <SettingRight>
            <ChevronRight />
          </SettingRight>
        </SettingItem>
        <SettingItem onClick={() => navigate('/inventario')}>
          <SettingLeft>
            <Package />
            <SettingText>Inventario</SettingText>
          </SettingLeft>
          <SettingRight>
            <ChevronRight />
          </SettingRight>
        </SettingItem>
      </Section>

      <Section>
        <SectionTitle>Configuración</SectionTitle>
        <SettingItem onClick={() => toggleSetting('notifications')}>
          <SettingLeft>
            <Bell />
            <SettingText>Notificaciones</SettingText>
          </SettingLeft>
          <SettingRight>
            <Toggle $active={settings.notifications} onClick={(e) => e.stopPropagation()} />
          </SettingRight>
        </SettingItem>
        <SettingItem onClick={toggleDarkMode}>
          <SettingLeft>
            <Moon />
            <SettingText>Modo Oscuro</SettingText>
          </SettingLeft>
          <SettingRight>
            <Toggle $active={isDarkMode} onClick={(e) => { e.stopPropagation(); toggleDarkMode(); }} />
          </SettingRight>
        </SettingItem>
        <SettingItem>
          <SettingLeft>
            <Globe />
            <SettingText>Idioma</SettingText>
          </SettingLeft>
          <SettingRight>
            <SettingValue>{settings.language}</SettingValue>
            <ChevronRight />
          </SettingRight>
        </SettingItem>
      </Section>

      <Section>
        <SectionTitle>Soporte</SectionTitle>
        <SettingItem>
          <SettingLeft>
            <Shield />
            <SettingText>Privacidad y Seguridad</SettingText>
          </SettingLeft>
          <SettingRight>
            <ChevronRight />
          </SettingRight>
        </SettingItem>
        <SettingItem>
          <SettingLeft>
            <HelpCircle />
            <SettingText>Ayuda y Soporte</SettingText>
          </SettingLeft>
          <SettingRight>
            <ChevronRight />
          </SettingRight>
        </SettingItem>
      </Section>

      <LogoutButton onClick={handleLogout}>
        <LogOut />
        Cerrar Sesión
      </LogoutButton>

      <VersionText>Dr. Desk v1.0.0</VersionText>
    </PageContainer>
  );
};

export default Perfil;
